import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { AdminPageHeader, AdminStatusBadge } from './AdminUi';
import './admin.css';

function Field({ label, value }) {
  return (
    <div className="col-md-6">
      <div className="small text-muted mb-1">{label}</div>
      <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>{value ?? '—'}</div>
    </div>
  );
}

function Stat({ label, value, to, hint }) {
  const inner = (
    <div className={`admin-card p-3 text-center h-100 ${to ? 'admin-stat-link' : ''}`}>
      <div className="fw-bold fs-4 mb-0">{value ?? 0}</div>
      <div className="small text-muted">{label}</div>
      {hint ? <div className="small text-primary mt-1">{hint}</div> : null}
    </div>
  );
  if (to) {
    return (
      <div className="col-6 col-md-3">
        <Link to={to} className="text-decoration-none text-reset d-block h-100">
          {inner}
        </Link>
      </div>
    );
  }
  return <div className="col-6 col-md-3">{inner}</div>;
}

function DocLink({ label, url }) {
  if (!url) {
    return (
      <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
        <span>{label}</span>
        <span className="text-muted small">Not uploaded</span>
      </div>
    );
  }
  return (
    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
      <span>{label}</span>
      <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary rounded-pill px-3">
        View
      </a>
    </div>
  );
}

export default function AdminDoctorDetail() {
  const { doctorId } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`admin/doctors/${doctorId}/`);
      setDoctor(r.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load doctor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId]);

  const setVerification = async (verification_status) => {
    setBusy(true);
    try {
      const r = await api.patch(`admin/doctors/${doctorId}/`, { verification_status });
      setDoctor(r.data);
      if (verification_status === 'APPROVED') {
        toast.success('Doctor approved — now live in patient app');
      } else if (verification_status === 'REJECTED') {
        toast.error('Doctor rejected — reason sent by email');
      } else {
        toast.success(`Doctor marked ${verification_status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }
  if (!doctor) {
    return (
      <div>
        <Link to="/admin/doctors" className="btn btn-outline-secondary rounded-pill mb-3">← Back</Link>
        <div className="alert alert-warning">Doctor not found</div>
      </div>
    );
  }

  const initial = (doctor.name || 'D').slice(0, 1).toUpperCase();

  return (
    <div>
      <AdminPageHeader title={doctor.name || 'Doctor profile'} subtitle={doctor.email}>
        <Link to="/admin/doctors" className="btn btn-outline-secondary rounded-pill px-3">← Back to doctors</Link>
        <button
          type="button"
          className="btn btn-success rounded-pill px-3"
          disabled={busy || doctor.verification_status === 'APPROVED'}
          onClick={() => setVerification('APPROVED')}
        >
          Approve
        </button>
        <button
          type="button"
          className="btn btn-outline-danger rounded-pill px-3"
          disabled={busy || doctor.verification_status === 'REJECTED'}
          onClick={() => setVerification('REJECTED')}
        >
          Reject
        </button>
      </AdminPageHeader>

      <div className="row g-3 mb-3">
        <Stat label="Appointments" value={doctor.appointments_count} />
        <Stat label="Posts" value={doctor.posts_count} />
        <Stat
          label="Avg rating (out of 5)"
          value={`${Number(doctor.average_rating || 0).toFixed(1)}/5`}
        />
        <Stat
          label="Reviews"
          value={doctor.total_reviews ?? 0}
          to={`/admin/doctors/${doctorId}/reviews`}
          hint="View all →"
        />
      </div>

      <div className="admin-card admin-doctor-hero p-4 mb-3">
        <div className="admin-profile-photo">
          {doctor.image ? <img src={doctor.image} alt={doctor.name || 'Doctor'} /> : initial}
        </div>
        <div className="admin-doctor-hero-info min-w-0">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <h5 className="fw-bold mb-0 text-truncate">{doctor.name}</h5>
            <AdminStatusBadge status={doctor.verification_status} />
          </div>
          <p className="text-muted small mb-2">
            {doctor.specialization || 'No specialization'}
            {doctor.qualification ? ` · ${doctor.qualification}` : ''}
          </p>
          <div className="small">
            <span className="fw-semibold text-warning">★ {Number(doctor.average_rating || 0).toFixed(1)}</span>
            <span className="text-muted"> out of 5</span>
            <span className="text-muted"> · </span>
            <Link to={`/admin/doctors/${doctorId}/reviews`} className="text-decoration-none">
              {doctor.total_reviews ?? 0} reviews
            </Link>
          </div>
        </div>
      </div>

      <div className="admin-card p-4 mb-3">
        <h6 className="fw-bold mb-3">Professional details</h6>
        <div className="row g-3">
          <Field label="Phone" value={doctor.phone_number} />
          <Field label="Email" value={doctor.email} />
          <Field label="Qualification" value={doctor.qualification} />
          <Field label="Experience (years)" value={doctor.experience} />
          <Field
            label="Video fee"
            value={doctor.consultation_fees != null ? `$${doctor.consultation_fees}` : null}
          />
          <Field
            label="Audio fee"
            value={doctor.audio_consultation_fees != null ? `$${doctor.audio_consultation_fees}` : null}
          />
          <Field label="Address" value={doctor.address} />
          <Field
            label="Last login"
            value={doctor.last_login ? new Date(doctor.last_login).toLocaleString() : null}
          />
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="admin-card p-4 h-100">
            <h6 className="fw-bold mb-3">Verification documents</h6>
            <DocLink label="Identity document" url={doctor.identity_document} />
            <DocLink label="Medical certificate" url={doctor.medical_certificate} />
            <DocLink label="Experience certificate" url={doctor.experience_certificate} />
          </div>
        </div>
        <div className="col-lg-6">
          <div className="admin-card p-4 h-100">
            <h6 className="fw-bold mb-3">Bank details</h6>
            <div className="row g-3">
              <Field label="Account holder" value={doctor.bank_account_holder} />
              <Field label="Bank name" value={doctor.bank_name} />
              <Field label="Account number" value={doctor.bank_account_number} />
              <Field label="IFSC / Routing" value={doctor.bank_routing_or_ifsc} />
              <Field label="Account type" value={doctor.bank_account_type} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
